/**
 * Universal Theme System - Single Source of Truth
 * Consolidates all theme configuration and provides theme-aware utilities
 */

import { withPhoneTypeScale } from './phoneTypeScale';
import { createTheme, Theme, alpha } from '@mui/material/styles';
import { componentOverrides } from './themeExtensions';

// Base brand colors (shared between themes) - Mercury.com neutral palette
export const baseBrandColors = {
  primary: {
    50: '#FAFAFA',   // Near white - subtle backgrounds
    100: '#F5F5F5',  // Light gray backgrounds
    200: '#E5E5E5',  // Dividers and borders
    300: '#D4D4D4',  // Disabled states
    400: '#A3A3A3',  // Muted secondary text
    500: '#171717',  // Primary brand black (Mercury style)
    600: '#0A0A0A',  // Darker black for emphasis
    700: '#000000',  // Pure black
    800: '#000000',  // Pure black
    900: '#000000',  // Pure black
  },
  // Semantic colors - ONLY for financial data (professional standard)
  success: {
    main: '#00A63F',      // Vivid green - strong contrast on light backgrounds
    light: '#00C851',     // Lighter for dark mode
    dark: '#008830',      // Darker emphasis
  },
  error: {
    main: '#DD0000',      // Standard red - more professional
    light: '#FF4444',     // Lighter for dark mode
    dark: '#AA0000',      // Darker emphasis
  },

  // REMOVED: warning, info (use grayscale + icons instead)
  // Professional terminals don't use yellow/orange for warnings

  // Financial data colors (strict functional use)
  financial: {
    positive: '#00AA00',  // Green for gains (matches success)
    negative: '#DD0000',  // Red for losses (matches error)
    neutral: '#6B7280',   // Gray for neutral

    // REMOVED: warning, accent (unprofessional decoration)
  },

  // Common UI colors - centralized to avoid hardcoding
  // Monochrome button palette (professional terminal aesthetic)
  buttons: {
    primary: {
      main: '#171717',      // Primary black button
      hover: '#2a2a2a',     // Hover state (slightly lighter)
      light: '#404040',     // Light variant
      dark: '#000000',      // Dark variant (pure black)
      contrastText: '#FFFFFF', // White text on dark buttons
    },
    // Inverted for dark mode (white buttons)
    primaryInverted: {
      main: '#FFFFFF',      // White button for dark mode
      hover: '#F5F5F5',     // Hover state
      light: '#FAFAFA',     // Light variant
      dark: '#E5E5E5',      // Pressed state
      contrastText: '#171717', // Dark text on light buttons
    },
    // Secondary outlined style
    secondary: {
      main: 'transparent',
      hover: 'rgba(23, 23, 23, 0.04)',
      border: '#D4D4D4',
      borderHover: '#171717',
      text: '#171717',
    },
    // Keep green for backwards compatibility but mark as deprecated
    // TODO: Remove after migration complete
    green: {
      main: '#171717',      // Migrated to monochrome
      hover: '#2a2a2a',     // Migrated to monochrome
      light: '#404040',     // Migrated to monochrome
      dark: '#000000',      // Migrated to monochrome
    }
  },

  // Gradients - centralized for consistency (professional monochrome only)
  gradients: {
    heroLight: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
    heroDark: 'linear-gradient(135deg, #1e1e1e 0%, #2a2a2a 100%)',
    // Overlay gradients
    overlayLight: 'rgba(255, 255, 255, 0.95)',
    overlayDark: 'rgba(0, 0, 0, 0.85)',
  },

  // Social media brand colors
  social: {
    twitter: '#1DA1F2',
    reddit: '#FF4500',
    linkedin: '#0A66C2',
    facebook: '#1877F2',
  },

  // Caution/attention colors - use sparingly, prefer grayscale
  // For non-financial warnings, use grayscale with icons instead
  caution: {
    main: '#6B7280',        // Gray - neutral attention
    light: '#9CA3AF',
    dark: '#4B5563',
  },
};

// Avatar color palette - centralized for user profiles (grayscale for professional look)
export const avatarColorPalette = [
  '#1f2937', '#374151', '#4b5563', '#6b7280',
  '#9ca3af', '#111827', '#1f2937', '#374151',
  '#4b5563', '#6b7280', '#1f2937', '#374151',
  '#4b5563', '#6b7280', '#9ca3af', '#111827',
];

/**
 * Typography System - Single Source of Truth
 *
 * Based on financial industry best practices:
 * - Bloomberg Terminal: Custom Matthew Carter fonts with tabular figures
 * - Mercury.com: Inter/SF Pro, clean hierarchy, 14-16px base
 * - Refinitiv/Reuters: Roboto, strict size scale, dense layouts
 *
 * Key principles:
 * 1. Use sans-serif for UI, monospace for financial data
 * 2. Tabular figures (lining-nums tabular-nums) for number alignment
 * 3. Compact sizing for data-dense displays (13-14px base)
 * 4. Limited font weights (400, 500, 600, 700) to reduce cognitive load
 */
export const typography = {
  // Font families - industry standard stack
  fontFamily: {
    // Primary: Roboto - Google's professional sans-serif
    // Fallback chain: Helvetica → Arial → system sans-serif
    primary: '"Roboto", "Helvetica", "Arial", sans-serif',

    // Monospace: JetBrains Mono - designed for code/data readability
    // Features: ligatures disabled, tabular figures, distinct characters
    mono: '"JetBrains Mono", "Roboto Mono", "Courier New", monospace',

    // Alternative: Inter - if switching from Roboto (modern FinTech standard)
    // inter: '"Inter", "Helvetica Neue", "Arial", sans-serif',
  },

  // Font weights - limited palette for consistency
  fontWeight: {
    regular: 400,    // Body text, descriptions
    medium: 500,     // Emphasis, secondary headers
    semibold: 600,   // Buttons, labels, subheaders
    bold: 700,       // Headers, important values
  },

  // Font size scale - based on 1.125 ratio (Major Second)
  // Professional/data-dense sizing (smaller than consumer apps)
  fontSize: {
    xs: '0.6875rem',    // 11px - metadata, timestamps, overlines
    sm: '0.75rem',      // 12px - captions, table headers, badges
    base: '0.8125rem',  // 13px - body text, table cells (professional standard)
    md: '0.875rem',     // 14px - emphasized body, buttons
    lg: '1rem',         // 16px - h6, section headers
    xl: '1.125rem',     // 18px - h5, card titles
    '2xl': '1.25rem',   // 20px - h4, important headers
    '3xl': '1.5rem',    // 24px - h3, page sections
    '4xl': '1.75rem',   // 28px - h2, major sections
    '5xl': '2rem',      // 32px - h1, page titles
  },

  // Line heights - tighter for data-dense UIs
  lineHeight: {
    none: 1,          // Single line items
    tight: 1.2,       // Headers, compact text
    snug: 1.3,        // Table cells, dense content
    normal: 1.4,      // Body text (not 1.5-1.7 like consumer apps)
    relaxed: 1.5,     // Descriptions, longer text blocks
  },

  // Letter spacing - subtle adjustments
  letterSpacing: {
    tighter: '-0.02em',  // Large headers
    tight: '-0.01em',    // Medium headers
    normal: '0',         // Body text
    wide: '0.01em',      // Body with slight tracking
    wider: '0.02em',     // Buttons, small text
    widest: '0.08em',    // Overlines, labels (uppercase)
  },

  // CSS for tabular/lining figures (critical for financial data)
  // Use on any element displaying numbers that should align vertically
  numericStyle: {
    fontVariantNumeric: 'tabular-nums lining-nums',
    fontFeatureSettings: '"tnum" 1, "lnum" 1',
  },
};

// Density mode type (matches ThemeContext)
export type DensityMode = 'comfortable' | 'normal' | 'compact';

/**
 * Responsive Typography System
 *
 * Uses CSS clamp() for fluid typography that scales with viewport width.
 * Each density mode has its own scale for body text, with headers scaling proportionally.
 *
 * Format: clamp(min, preferred, max)
 * - min: Minimum font size (prevents too small on narrow viewports)
 * - preferred: Fluid size using rem + vw (scales with viewport)
 * - max: Maximum font size (prevents too large on wide viewports)
 *
 * WCAG 1.4.4 Compliance: max ≤ 2.5× min for all sizes
 */
export const responsiveTypography = {
  // Compact: For data-dense displays (Bloomberg Terminal style)
  // Body: 12-13px range, slightly tighter line heights but still readable
  compact: {
    xs: 'clamp(0.625rem, 0.6rem + 0.1vw, 0.6875rem)',      // 10-11px
    sm: 'clamp(0.6875rem, 0.65rem + 0.15vw, 0.75rem)',     // 11-12px
    base: 'clamp(0.75rem, 0.7rem + 0.15vw, 0.8125rem)',    // 12-13px
    md: 'clamp(0.8125rem, 0.75rem + 0.2vw, 0.875rem)',     // 13-14px
    lg: 'clamp(0.875rem, 0.8rem + 0.2vw, 1rem)',           // 14-16px
    xl: 'clamp(1rem, 0.95rem + 0.2vw, 1.125rem)',          // 16-18px
    '2xl': 'clamp(1.125rem, 1.05rem + 0.25vw, 1.25rem)',   // 18-20px
    '3xl': 'clamp(1.25rem, 1.15rem + 0.3vw, 1.5rem)',      // 20-24px
    '4xl': 'clamp(1.5rem, 1.35rem + 0.35vw, 1.75rem)',     // 24-28px
    '5xl': 'clamp(1.75rem, 1.6rem + 0.4vw, 2rem)',         // 28-32px
    lineHeight: {
      tight: 1.2,       // Was 1.15 - headers still compact
      snug: 1.35,       // Was 1.25 - better paragraph readability
      normal: 1.45,     // Was 1.35 - comfortable body text
      relaxed: 1.55,    // Was 1.45 - descriptions
    },
  },

  // Normal: Balanced for everyday use (default)
  // Body: 13-14px range, standard line heights
  normal: {
    xs: 'clamp(0.6875rem, 0.65rem + 0.1vw, 0.75rem)',      // 11-12px
    sm: 'clamp(0.75rem, 0.7rem + 0.15vw, 0.8125rem)',      // 12-13px
    base: 'clamp(0.8125rem, 0.75rem + 0.15vw, 0.875rem)',  // 13-14px
    md: 'clamp(0.875rem, 0.8rem + 0.2vw, 0.9375rem)',      // 14-15px
    lg: 'clamp(0.9375rem, 0.875rem + 0.2vw, 1.0625rem)',   // 15-17px
    xl: 'clamp(1.0625rem, 1rem + 0.2vw, 1.1875rem)',       // 17-19px
    '2xl': 'clamp(1.1875rem, 1.1rem + 0.25vw, 1.3125rem)', // 19-21px
    '3xl': 'clamp(1.375rem, 1.25rem + 0.3vw, 1.5625rem)',  // 22-25px
    '4xl': 'clamp(1.625rem, 1.5rem + 0.35vw, 1.875rem)',   // 26-30px
    '5xl': 'clamp(1.875rem, 1.7rem + 0.4vw, 2.125rem)',    // 30-34px
    lineHeight: {
      tight: 1.2,
      snug: 1.3,
      normal: 1.4,
      relaxed: 1.5,
    },
  },

  // Comfortable: For relaxed reading (larger displays, accessibility)
  // Body: 14-15px range, relaxed line heights
  comfortable: {
    xs: 'clamp(0.75rem, 0.7rem + 0.1vw, 0.8125rem)',       // 12-13px
    sm: 'clamp(0.8125rem, 0.75rem + 0.15vw, 0.875rem)',    // 13-14px
    base: 'clamp(0.875rem, 0.8rem + 0.15vw, 0.9375rem)',   // 14-15px
    md: 'clamp(0.9375rem, 0.875rem + 0.2vw, 1rem)',        // 15-16px
    lg: 'clamp(1rem, 0.95rem + 0.2vw, 1.125rem)',          // 16-18px
    xl: 'clamp(1.125rem, 1.05rem + 0.2vw, 1.25rem)',       // 18-20px
    '2xl': 'clamp(1.25rem, 1.15rem + 0.25vw, 1.375rem)',   // 20-22px
    '3xl': 'clamp(1.5rem, 1.35rem + 0.3vw, 1.625rem)',     // 24-26px
    '4xl': 'clamp(1.75rem, 1.6rem + 0.35vw, 2rem)',        // 28-32px
    '5xl': 'clamp(2rem, 1.85rem + 0.4vw, 2.25rem)',        // 32-36px
    lineHeight: {
      tight: 1.25,
      snug: 1.35,
      normal: 1.45,
      relaxed: 1.55,
    },
  },
};

// Helper to get typography scale for a density mode
export const getTypographyScale = (density: DensityMode = 'normal') => {
  return responsiveTypography[density];
};

// Semantic color tokens for theme-aware components
export const createSemanticTokens = (mode: 'light' | 'dark') => {
  const isLight = mode === 'light';

  return {
    // Surface colors with elevation awareness
    surfaces: {
      primary: isLight ? '#ffffff' : '#121212',
      secondary: isLight ? '#f8f9fa' : '#1e1e1e',
      elevated1: isLight ? '#ffffff' : '#1f1f1f', // 1dp
      elevated2: isLight ? '#ffffff' : '#232323', // 2dp
      elevated3: isLight ? '#ffffff' : '#252525', // 3dp
      elevated4: isLight ? '#ffffff' : '#272727', // 4dp
      elevated6: isLight ? '#ffffff' : '#2c2c2c', // 6dp
      elevated8: isLight ? '#ffffff' : '#2e2e2e', // 8dp
      elevated12: isLight ? '#ffffff' : '#333333', // 12dp
      elevated16: isLight ? '#ffffff' : '#353535', // 16dp
      elevated24: isLight ? '#ffffff' : '#383838', // 24dp
    },

    // Interactive surface states
    interactive: {
      hover: isLight ? 'rgba(0, 0, 0, 0.04)' : 'rgba(255, 255, 255, 0.05)',
      selected: isLight ? 'rgba(5, 93, 173, 0.08)' : 'rgba(59, 127, 196, 0.16)',
      pressed: isLight ? 'rgba(0, 0, 0, 0.08)' : 'rgba(255, 255, 255, 0.08)',
      focus: isLight ? 'rgba(5, 93, 173, 0.12)' : 'rgba(59, 127, 196, 0.20)',
    },

    // Text with proper contrast
    text: {
      primary: isLight ? '#171717' : 'rgba(255, 255, 255, 0.87)',  // Mercury-style black in light mode
      // Dark mode at 0.62 white: 0.45 measured 4.41:1 on the dark surfaces, under
      // the 4.5:1 body-text minimum, on subtitles across /, /research, /chat and
      // /academy ([[BUGS #430]]).
      secondary: isLight ? '#6B7280' : 'rgba(255, 255, 255, 0.62)',
      disabled: isLight ? 'rgba(0, 0, 0, 0.38)' : 'rgba(255, 255, 255, 0.30)',
      hint: isLight ? 'rgba(0, 0, 0, 0.6)' : 'rgba(255, 255, 255, 0.50)',
    },

    // Border colors
    borders: {
      primary: isLight ? '#e0e0e0' : '#424242',
      secondary: isLight ? '#f0f0f0' : '#333333',
      divider: isLight ? 'rgba(0, 0, 0, 0.12)' : 'rgba(255, 255, 255, 0.12)',
    },

    // Special purpose backgrounds
    special: {
      agentSummary: isLight ? 'rgba(5, 93, 173, 0.04)' : 'rgba(59, 127, 196, 0.08)',
      tableHeader: isLight ? '#f8f9fa' : '#2a2a2a',
      codeBlock: isLight ? '#f6f8fa' : '#161b22',
      overlay: isLight ? 'rgba(0, 0, 0, 0.5)' : 'rgba(0, 0, 0, 0.7)',
    }
  };
};

// Create comprehensive light theme with optional density mode
/* The two names above, taught to TypeScript. Without this an `sx` prop keyed
 * `xxl` is a type error, and `theme.breakpoints.up('xxl')` does not compile. */
declare module '@mui/material/styles' {
  interface BreakpointOverrides {
    xxl: true;
    xxxl: true;
  }
}

export const createLightTheme = (density: DensityMode = 'normal'): Theme => {
  const tokens = createSemanticTokens('light');
  const scale = responsiveTypography[density];

  // Get base font size for rem calculations based on density
  const baseFontSize = density === 'compact' ? 12 : density === 'normal' ? 13 : 14;

  return withPhoneTypeScale(createTheme({
    // Desktop is its own regime, and until now the theme had no name for it
    // (ROADMAP "The Strategist result page…", item 8a). MUI's defaults stop at
    // `xl` = 1536; per Material Design 3's size classes (developer.android.com,
    // 2026) the large/extra-large regimes begin around 1200dp and a 1920 or
    // 2560 monitor is not a stretched tablet. The five defaults are restated
    // verbatim because declaring `values` replaces the set — omitting one
    // would silently move it to 0.
    breakpoints: {
      values: { xs: 0, sm: 600, md: 900, lg: 1200, xl: 1536, xxl: 1920, xxxl: 2560 },
    },
    palette: {
      mode: 'light',
      primary: {
        main: baseBrandColors.primary[500],
        light: baseBrandColors.primary[300],
        dark: baseBrandColors.primary[700],
        contrastText: '#ffffff',
      },
      // REMOVED: secondary color (professional terminals don't use decorative colors)

      // Semantic colors - ONLY for financial data
      success: {
        main: baseBrandColors.success.main,
        light: baseBrandColors.success.light,
        dark: baseBrandColors.success.dark,
      },
      error: {
        main: baseBrandColors.error.main,
        light: baseBrandColors.error.light,
        dark: baseBrandColors.error.dark,
      },

      // REMOVED: warning, info (use grayscale + icons for non-financial warnings)
      background: {
        default: tokens.surfaces.secondary,
        paper: tokens.surfaces.primary,
      },
      text: {
        primary: tokens.text.primary,
        secondary: tokens.text.secondary,
      },
      divider: tokens.borders.divider,
      // Professional grayscale (reduced from 10 to 7 shades)
      grey: {
        50: '#F9FAFB',   // Subtle backgrounds
        100: '#F3F4F6',  // Cards, headers
        200: '#E5E7EB',  // Borders
        300: '#D1D5DB',  // Disabled
        500: '#6B7280',  // Secondary text
        700: '#374151',  // Primary text
        900: '#111827',  // Headers
        // REMOVED: 400, 600, 800 (unnecessary gradation)
      }
    },
    typography: {
      fontFamily: typography.fontFamily.primary,
      fontSize: baseFontSize,  // Dynamic base size for rem calculations

      // Headers - using responsive typography scale
      h1: {
        fontSize: scale['5xl'],
        fontWeight: typography.fontWeight.bold,
        letterSpacing: typography.letterSpacing.tighter,
        lineHeight: scale.lineHeight.tight,
      },
      h2: {
        fontSize: scale['4xl'],
        fontWeight: typography.fontWeight.bold,
        letterSpacing: typography.letterSpacing.tight,
        lineHeight: scale.lineHeight.tight,
      },
      h3: {
        fontSize: scale['3xl'],
        fontWeight: typography.fontWeight.bold,
        lineHeight: scale.lineHeight.snug,
      },
      h4: {
        fontSize: scale['2xl'],
        fontWeight: typography.fontWeight.semibold,
        lineHeight: scale.lineHeight.snug,
      },
      h5: {
        fontSize: scale.xl,
        fontWeight: typography.fontWeight.semibold,
        lineHeight: scale.lineHeight.snug,
      },
      h6: {
        fontSize: scale.lg,
        fontWeight: typography.fontWeight.semibold,
        lineHeight: scale.lineHeight.snug,
      },

      // Body text - using responsive typography scale
      body1: {
        fontSize: scale.md,
        lineHeight: scale.lineHeight.normal,
        letterSpacing: typography.letterSpacing.wide,
      },
      body2: {
        fontSize: scale.base,
        lineHeight: scale.lineHeight.snug,
        letterSpacing: typography.letterSpacing.wide,
      },

      // UI elements
      button: {
        fontSize: scale.md,
        fontWeight: typography.fontWeight.semibold,
        textTransform: 'none' as const,
        letterSpacing: typography.letterSpacing.wider,
      },

      // Data display - monospace for financial figures
      caption: {
        fontSize: scale.sm,
        lineHeight: scale.lineHeight.tight,
        letterSpacing: typography.letterSpacing.wider,
        fontFamily: typography.fontFamily.mono,
      },

      // Overline - for labels/metadata
      overline: {
        fontSize: scale.xs,
        fontWeight: typography.fontWeight.bold,
        textTransform: 'uppercase' as const,
        letterSpacing: typography.letterSpacing.widest,
        lineHeight: scale.lineHeight.tight,
      },
    },
    components: {
      ...componentOverrides,
      // Override with theme-aware versions
      MuiAppBar: {
        styleOverrides: {
          root: {
            backgroundColor: tokens.surfaces.primary,
            borderBottom: `1px solid ${tokens.borders.primary}`,
          },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          root: {
            fontSize: scale.base,
            padding: density === 'compact' ? '6px 8px' : density === 'normal' ? '8px 10px' : '10px 12px',
            height: density === 'compact' ? '36px' : density === 'normal' ? '44px' : '52px',
            borderColor: tokens.borders.divider,
            lineHeight: scale.lineHeight.snug,
            ...typography.numericStyle,
          },
          head: {
            fontSize: scale.sm,
            fontWeight: typography.fontWeight.bold,
            padding: density === 'compact' ? '6px 8px' : '8px 10px',
            height: density === 'compact' ? '32px' : density === 'normal' ? '40px' : '48px',
            backgroundColor: tokens.special.tableHeader,
            color: tokens.text.primary,
            textTransform: 'uppercase',
            letterSpacing: typography.letterSpacing.wider,
          },
          // Numeric columns - right-aligned with monospace
          alignRight: {
            fontFamily: typography.fontFamily.mono,
            ...typography.numericStyle,
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundImage: 'none',
            borderRadius: 0,
            backgroundColor: tokens.surfaces.primary,
          },
        },
      },
      MuiTooltip: {
        styleOverrides: {
          tooltip: {
            backgroundColor: '#171717',  // Dark background for light mode tooltips
            color: '#ffffff',            // White text for maximum contrast
            fontSize: scale.sm,
            fontWeight: typography.fontWeight.regular,
            padding: '8px 12px',
            borderRadius: 4,
            boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
          },
          arrow: {
            color: '#171717',
          },
        },
      },
    },
  }));
};

// Create comprehensive dark theme with optional density mode
export const createDarkTheme = (density: DensityMode = 'normal'): Theme => {
  const tokens = createSemanticTokens('dark');
  const scale = responsiveTypography[density];

  // Get base font size for rem calculations based on density
  const baseFontSize = density === 'compact' ? 12 : density === 'normal' ? 13 : 14;

  return withPhoneTypeScale(createTheme({
    // Desktop is its own regime, and until now the theme had no name for it
    // (ROADMAP "The Strategist result page…", item 8a). MUI's defaults stop at
    // `xl` = 1536; per Material Design 3's size classes (developer.android.com,
    // 2026) the large/extra-large regimes begin around 1200dp and a 1920 or
    // 2560 monitor is not a stretched tablet. The five defaults are restated
    // verbatim because declaring `values` replaces the set — omitting one
    // would silently move it to 0.
    breakpoints: {
      values: { xs: 0, sm: 600, md: 900, lg: 1200, xl: 1536, xxl: 1920, xxxl: 2560 },
    },
    palette: {
      mode: 'dark',
      primary: {
        main: '#FFFFFF', // White for dark mode (high contrast)
        light: '#F5F5F5',
        dark: '#E5E5E5',
        contrastText: '#000000',
      },
      // REMOVED: secondary color (professional terminals use grayscale)

      // Semantic colors - lighter for dark mode visibility
      success: {
        main: '#00C851',  // Brighter green for dark backgrounds
        light: '#00E862',
        dark: '#00AA00',
      },
      error: {
        main: '#FF4444',  // Brighter red for dark backgrounds
        light: '#FF6666',
        dark: '#DD0000',
      },

      // REMOVED: warning, info (use grayscale)
      background: {
        default: tokens.surfaces.secondary,
        paper: tokens.surfaces.primary,
      },
      text: {
        primary: tokens.text.primary,
        secondary: tokens.text.secondary,
      },
      divider: tokens.borders.divider,
      // Professional grayscale for dark mode (Bloomberg Terminal style)
      grey: {
        50: '#111827',   // Darkest - backgrounds
        100: '#1F2937',  // Cards
        200: '#374151',  // Borders
        300: '#4B5563',  // Disabled
        500: '#9CA3AF',  // Secondary text
        700: '#D1D5DB',  // Primary text
        900: '#F9FAFB',  // Headers
        // REMOVED: 400, 600, 800 (unnecessary gradation)
      }
    },
    typography: {
      fontFamily: typography.fontFamily.primary,
      fontSize: baseFontSize,  // Dynamic base size for rem calculations

      // Headers - using responsive typography scale
      h1: {
        fontSize: scale['5xl'],
        fontWeight: typography.fontWeight.bold,
        letterSpacing: typography.letterSpacing.tighter,
        lineHeight: scale.lineHeight.tight,
      },
      h2: {
        fontSize: scale['4xl'],
        fontWeight: typography.fontWeight.bold,
        letterSpacing: typography.letterSpacing.tight,
        lineHeight: scale.lineHeight.tight,
      },
      h3: {
        fontSize: scale['3xl'],
        fontWeight: typography.fontWeight.bold,
        lineHeight: scale.lineHeight.snug,
      },
      h4: {
        fontSize: scale['2xl'],
        fontWeight: typography.fontWeight.semibold,
        lineHeight: scale.lineHeight.snug,
      },
      h5: {
        fontSize: scale.xl,
        fontWeight: typography.fontWeight.semibold,
        lineHeight: scale.lineHeight.snug,
      },
      h6: {
        fontSize: scale.lg,
        fontWeight: typography.fontWeight.semibold,
        lineHeight: scale.lineHeight.snug,
      },

      // Body text - using responsive typography scale
      body1: {
        fontSize: scale.md,
        lineHeight: scale.lineHeight.normal,
        letterSpacing: typography.letterSpacing.wide,
      },
      body2: {
        fontSize: scale.base,
        lineHeight: scale.lineHeight.snug,
        letterSpacing: typography.letterSpacing.wide,
      },

      // UI elements
      button: {
        fontSize: scale.md,
        fontWeight: typography.fontWeight.semibold,
        textTransform: 'none' as const,
        letterSpacing: typography.letterSpacing.wider,
      },

      // Data display - monospace for financial figures
      caption: {
        fontSize: scale.sm,
        lineHeight: scale.lineHeight.tight,
        letterSpacing: typography.letterSpacing.wider,
        fontFamily: typography.fontFamily.mono,
      },

      // Overline - for labels/metadata
      overline: {
        fontSize: scale.xs,
        fontWeight: typography.fontWeight.bold,
        textTransform: 'uppercase' as const,
        letterSpacing: typography.letterSpacing.widest,
        lineHeight: scale.lineHeight.tight,
      },
    },
    components: {
      ...componentOverrides,
      // Override with theme-aware versions for dark mode
      MuiAppBar: {
        styleOverrides: {
          root: {
            backgroundColor: tokens.surfaces.primary,
            borderBottom: `1px solid ${tokens.borders.primary}`,
          },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          root: {
            fontSize: scale.base,
            padding: density === 'compact' ? '6px 8px' : density === 'normal' ? '8px 10px' : '10px 12px',
            height: density === 'compact' ? '36px' : density === 'normal' ? '44px' : '52px',
            borderColor: tokens.borders.divider,
            lineHeight: scale.lineHeight.snug,
            ...typography.numericStyle,
          },
          head: {
            fontSize: scale.sm,
            fontWeight: typography.fontWeight.bold,
            padding: density === 'compact' ? '6px 8px' : '8px 10px',
            height: density === 'compact' ? '32px' : density === 'normal' ? '40px' : '48px',
            backgroundColor: tokens.special.tableHeader,
            color: tokens.text.primary,
            textTransform: 'uppercase',
            letterSpacing: typography.letterSpacing.wider,
          },
          // Numeric columns - right-aligned with monospace
          alignRight: {
            fontFamily: typography.fontFamily.mono,
            ...typography.numericStyle,
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundImage: 'none',
            borderRadius: 0,
            backgroundColor: tokens.surfaces.primary,
          },
        },
      },
      MuiTooltip: {
        styleOverrides: {
          tooltip: {
            backgroundColor: '#f5f5f5',  // Light gray background for dark mode tooltips
            color: '#171717',            // Dark text for maximum contrast
            fontSize: scale.sm,
            fontWeight: typography.fontWeight.regular,
            padding: '8px 12px',
            borderRadius: 4,
            boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
          },
          arrow: {
            color: '#f5f5f5',
          },
        },
      },
    },
  }));
};

// Theme-aware utility functions
export const getThemeValue = <T>(lightValue: T, darkValue: T, mode: 'light' | 'dark'): T => {
  return mode === 'dark' ? darkValue : lightValue;
};

export const getSemanticColor = (token: string, mode: 'light' | 'dark') => {
  const tokens = createSemanticTokens(mode);
  // Navigate nested object by token path (e.g., 'surfaces.primary')
  const path = token.split('.');
  let value: any = tokens;
  for (const key of path) {
    value = value[key];
  }
  return value;
};

// Create alpha variations of theme colors
export const createAlphaVariant = (color: string, opacity: number) => {
  return alpha(color, opacity);
};
